import axios from "axios";
import api, { ApiResponse } from "./../api";
import {
  BolUploadRequestModel,
  DriversResponseModel,
  PickupAssignRequestModel,
  PickupCancelRequestModel,
  PickupDocumentModel,
  PickupLogItem,
  PickupRequestModel,
  PickupRescheduleModel,
  PickupsLogFilterModel,
  PickupsResponseContentModel,
  PickupsResponseModel,
  PickupSummaryDto,
  QuickPickupCreateModel
} from "./models";
import { PickupStatus } from "../common/route";
import { PageResponseModel } from "../common/models";

export const REQUEST_ID: {
  [key: string]: string;
} = {
  GET_PICKUPS: "get_pickups",
  GET_DRIVERS: "get_drivers",
  GET_DRIVER: "get_driver",
  GET_PICKUP_LOG: "get_pickup_log",
  GET_PICKUP: "get_pickup",
  GENERATE_PICKUP_DOCUMENTS: "generate_pickup_documents",
  SEND_PICKUP_DOCUMENTS: "send_pickup_documents",
};

class PickupService {
  private source: any;
  private static instance: PickupService;
  private constructor() {
    this.source = {};
  }
  static getInstance(): PickupService {
    if (!PickupService.instance) {
      PickupService.instance = new PickupService();
    }
    return PickupService.instance;
  };

  public cancelRequest = (requestId: string) => {
    this.source[requestId] && this.source[requestId].cancel();
  }

  public editPickup = async (
    request: PickupRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.put("/pickups/update", request);
  };

  public createPickup = async (
    request: PickupRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.post("/pickups", request);
  };

  public afterUploadPickupBOL = async (
    pickupId: string,
    formData: FormData
  ): Promise<ApiResponse<any>> => {
    return await api.post(`/pickups/${pickupId}/bol`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  public getPickups = async (
    terminal: number | null,
    filter: {
      statuses: PickupStatus[],
      expiredOnly: boolean,
      showFuture: boolean,
      search: string,
      dateRange?: { startDate: string, endDate: string },
    },
    pagination?: {
      lastIds?: string,
      pageSize?: number,
    },
  ): Promise<ApiResponse<PickupsResponseModel>> => {
    const params = new URLSearchParams();
    terminal && params.append("terminalCode", terminal.toString());
    filter.expiredOnly && params.append("expiredOnly", filter.expiredOnly.toString());
    filter.showFuture && params.append("futureDate", filter.showFuture.toString());
    filter.search && params.append("search", filter.search);
    if (filter.statuses?.length > 0) params.append("statuses", filter.statuses.toString());
    if (filter.dateRange) {
      const { startDate, endDate } = filter.dateRange;
      if(startDate && startDate.length > 0) params.append("dateFrom", startDate);
      if(endDate && endDate.length > 0) params.append("dateTo", endDate);
    }
    pagination?.lastIds && params.append("lastIds", pagination.lastIds);
    pagination?.pageSize && params.append("pageSize", pagination.pageSize.toString());
    this.cancelRequest(REQUEST_ID.GET_PICKUPS);
    this.source[REQUEST_ID.GET_PICKUPS] = axios.CancelToken.source();
    return await api.get("/pickups", { params, cancelToken: this.source[REQUEST_ID.GET_PICKUPS].token });
  }

  public changePickup = async (
    request: PickupAssignRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.put("/pickups", request);
  };

  public changePickupStatus = async (
    pickup: {
      pickupId: string;
      status: PickupStatus;
      note?: string;
    },
  ): Promise<ApiResponse<any>> => {
    return await api.put("/pickups/update/status", pickup);
  };

  public changePickups = async (
    request: PickupAssignRequestModel[]
  ): Promise<ApiResponse<any>> => {
    return await api.put("/pickups/drivers", request);
  };

  public getDrivers = async (
    zip: string,
    lastIds?: string
  ): Promise<ApiResponse<DriversResponseModel>> => {
    const params = new URLSearchParams();
    params.append("postalCode", zip);
    lastIds && params.append("lastIds", lastIds);
    this.cancelRequest(REQUEST_ID.GET_DRIVERS);
    this.source[REQUEST_ID.GET_DRIVERS] = axios.CancelToken.source();
    return await api.get("/pickups/drivers", { params, cancelToken: this.source[REQUEST_ID.GET_DRIVERS].token });
  };

  public getDriver = async (
    id: number
  ): Promise<ApiResponse<any>> => {
    this.cancelRequest(REQUEST_ID.GET_DRIVER);
    this.source[REQUEST_ID.GET_DRIVER] = axios.CancelToken.source();
    return await api.get(`/pickups/driverBy/${encodeURIComponent(id)}`, { cancelToken: this.source[REQUEST_ID.GET_DRIVER].token });
  };

  public getPickupLog = async (
    pickupId: string
  ): Promise<ApiResponse<PickupLogItem[]>> => {
    const params = new URLSearchParams();
    params.append("pickupId", pickupId);
    this.cancelRequest(REQUEST_ID.GET_PICKUP_LOG);
    this.source[REQUEST_ID.GET_PICKUP_LOG] = axios.CancelToken.source();
    return await api.get("/pickups/activity-log", { params, cancelToken: this.source[REQUEST_ID.GET_PICKUP_LOG].token });
  };

  public getPickupsLog = async (
    filter: PickupsLogFilterModel,
    page?: number
  ): Promise<ApiResponse<PageResponseModel<PickupLogItem>>> => {
    const params = new URLSearchParams();
    for (const property in filter) {
      if (!property) continue;
      filter[property as keyof PickupsLogFilterModel] && params.append(property, filter[property as keyof PickupsLogFilterModel] || "");
    }
    page && params.append("page", page.toString());
    this.cancelRequest(REQUEST_ID.GET_PICKUPS_LOG);
    this.source[REQUEST_ID.GET_PICKUPS_LOG] = axios.CancelToken.source();
    return await api.get("/pickups/all/activity-log", { params, cancelToken: this.source[REQUEST_ID.GET_PICKUPS_LOG].token });
  };

  public cancelPickup = async (
    request: PickupCancelRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.post("/pickups/cancel", request);
  };

  public reschedulePickup = async (
    pickupId: string,
    payload: PickupRescheduleModel
  ): Promise<ApiResponse<any>> => {
    return await api.post(`/pickups/reschedule/${pickupId}`, payload);
  };

  public completePickup = async (
    pickupId: string,
    probill: number | string | null | undefined
  ): Promise<ApiResponse<any>> => {
    const probillNumber = probill ? parseInt(probill.toString()) : null;
    return await api.post("/pickups/complete", { pickupId, probillNumber });
  };

  public async getInboundPickups(
    originTerminalCode?: number,
    destinationTerminalCode?: number | null,
    statuses?: string[],
    pageNumber: number = 0,
    pageSize: number = 50
  ): Promise<ApiResponse<{ content: PickupsResponseContentModel[]; totalPages: number; totalElements: number; }>> {
    const params: any = {
      page: pageNumber,
      size: pageSize,
    };

    params.originTerminalCode = originTerminalCode;

    if (destinationTerminalCode) {
      params.destinationTerminalCode = destinationTerminalCode;
    }

    if (statuses && statuses.length > 0) {
      params.statuses = statuses.join(",");
    }

    return await api.get(`/pickups/inbound`, { params });
  }

  public async getInboundPickupsSummary(origin: number): Promise<ApiResponse<PickupSummaryDto[]>> {
    const params = new URLSearchParams();
    params.append("originTerminalCode", origin.toString());
    return await api.get(`/pickups/inbound/summary`, { params });
  }

  public getPickup = async (
    pickupNumber: string,
  ): Promise<ApiResponse<PickupsResponseContentModel>> => {
    this.cancelRequest(REQUEST_ID.GET_PICKUP);
    this.source[REQUEST_ID.GET_PICKUP] = axios.CancelToken.source();
    return await api.get(`/pickups/${pickupNumber}`, { cancelToken: this.source[REQUEST_ID.GET_PICKUP].token });
  };

  public getPickupsForBilling = async (
    trackingNumber: string | null,
    companyName: string | null,
    origin: number | null,
    destination: number | null,
    completedDateFrom: string | null,
    completedDateTo: string | null
  ): Promise<ApiResponse<PageResponseModel<PickupsResponseContentModel>>> => {
    return await api.get(`/pickups/billing`,
      {
        cancelToken: this.source[REQUEST_ID.billing_pickups],
        params: {
          trackingNumber,
          companyName,
          originTerminalCode: origin,
          destinationTerminalCode: destination,
          completedDateFrom,
          completedDateTo
        }
      })
  }

  public generatePickupDocuments = async (
    pickupNumber: string,
  ): Promise<ApiResponse<PickupDocumentModel[]>> => {
    this.cancelRequest(REQUEST_ID.GENERATE_PICKUP_DOCUMENTS);
    this.source[REQUEST_ID.GENERATE_PICKUP_DOCUMENTS] = axios.CancelToken.source();
    return await api.post(`/documents/pickup/${pickupNumber}`, { cancelToken: this.source[REQUEST_ID.GENERATE_PICKUP_DOCUMENTS].token });
  };

  public uploadBol = async (
    request: BolUploadRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.post("/proxy/xgsai/bol/recognition", request);
  };

  public pickupFeedback = async (
    request: PickupRequestModel
  ): Promise<ApiResponse<any>> => {
    return await api.post("/proxy/xgsai/bol/recognition/feedback", request);
  }

  public sendPickupDocuments = async (
    pickupNumber: string,
  ): Promise<ApiResponse<any>> => {
    this.cancelRequest(REQUEST_ID.SEND_PICKUP_DOCUMENTS);
    this.source[REQUEST_ID.SEND_PICKUP_DOCUMENTS] = axios.CancelToken.source();
    return await api.post(`/pickups/${pickupNumber}/upload-documents`, { cancelToken: this.source[REQUEST_ID.SEND_PICKUP_DOCUMENTS].token });
  };

  public createQuickPickup = async (
    request: QuickPickupCreateModel
  ): Promise<ApiResponse<any>> => {
    return await api.post("/pickups", request);
  };
};

export default PickupService;